import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Drama, Sparkles, Loader2, Plus, Check, Wand2, Users, ArrowRight, Play, Share2, Compass } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Spinner } from "@/components/Bits";
import { toast } from "sonner";

const asset = (u) => (!u ? null : u.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`);
const KINDS = ["hero", "villain", "sidekick", "npc", "monster", "anime", "robot", "creature"];

export default function VNStudio() {
  const navigate = useNavigate();
  const [chars, setChars] = useState([]);
  const [scenes, setScenes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState([]);
  const [premise, setPremise] = useState("");
  const [title, setTitle] = useState("");
  const [depth, setDepth] = useState("light");
  const [writing, setWriting] = useState(false);
  // quick create
  const [showNew, setShowNew] = useState(false);
  const [ncName, setNcName] = useState("");
  const [ncDesc, setNcDesc] = useState("");
  const [ncKind, setNcKind] = useState("hero");
  const [creating, setCreating] = useState(false);

  const loadChars = () => api.get("/characters").then(({ data }) => setChars(data || [])).catch(() => {});
  const loadScenes = () => api.get("/vn/scenes").then(({ data }) => setScenes(data.scenes || [])).catch(() => {});

  useEffect(() => {
    Promise.all([loadChars(), loadScenes()]).finally(() => setLoading(false));
  }, []);

  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const quickCreate = async () => {
    const desc = ncDesc.trim() || ncName.trim();
    if (!desc || creating) return;
    setCreating(true);
    try {
      const { data: c } = await api.post("/characters/generate", { prompt: ncDesc.trim() || `${ncName.trim()}, ${ncKind}`, kind: ncKind });
      if (ncName.trim()) { try { await api.patch(`/characters/${c.id}`, { name: ncName.trim() }); } catch { /* ignore */ } }
      try { await api.post(`/characters/${c.id}/art`, {}); } catch { /* art optional */ }
      toast.success(`${ncName.trim() || c.name} joined the cast! 🎭`);
      setNcName(""); setNcDesc(""); setNcKind("hero"); setShowNew(false);
      await loadChars();
      setSelected((s) => [...s, c.id]);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't create that character.");
    } finally { setCreating(false); }
  };

  const write = async () => {
    if (premise.trim().length < 4 || writing) return;
    setWriting(true);
    try {
      const { data } = await api.post("/vn/scenes/generate", { premise: premise.trim(), cast: selected, title: title.trim(), depth });
      toast.success("Your scene is written! 🎬");
      navigate(`/app/vn/${data.id}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "The writers' room got stuck — try again.");
    } finally { setWriting(false); }
  };

  if (loading) return <div className="min-h-[60vh] grid place-items-center"><Spinner /></div>;

  return (
    <div className="max-w-5xl mx-auto px-4 py-6" data-testid="vn-studio">
      <div className="flex items-center justify-between gap-3 mb-1">
        <div className="flex items-center gap-2">
          <Drama className="w-6 h-6 text-[hsl(var(--primary))]" />
          <h1 className="font-display text-3xl font-extrabold">VN Studio</h1>
        </div>
        <button onClick={() => navigate("/app/vn-gallery")} data-testid="vn-browse-wall"
          className="inline-flex items-center gap-1.5 rounded-full border border-[hsl(var(--border))] px-3.5 py-1.5 text-xs font-semibold hover:bg-[hsl(var(--secondary))]">
          <Compass className="w-3.5 h-3.5" /> Scene Wall
        </button>
      </div>
      <p className="text-sm text-[hsl(var(--muted-foreground))] mb-6">Pick a cast, give a premise, and the AI writes a branching, playable visual-novel scene — with AI backgrounds, save files and a scene gallery.</p>

      {/* Cast picker */}
      <section className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-5 mb-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="flex items-center gap-1.5 text-sm font-bold"><Users className="w-4 h-4 text-[hsl(var(--primary))]" /> Your cast <span className="text-[hsl(var(--muted-foreground))] font-normal">({selected.length} selected)</span></h2>
          <button onClick={() => setShowNew((v) => !v)} data-testid="vn-newchar-toggle"
            className="inline-flex items-center gap-1.5 rounded-full border border-[hsl(var(--border))] px-3 py-1.5 text-xs font-semibold hover:bg-[hsl(var(--secondary))]">
            <Plus className="w-3.5 h-3.5" /> New character
          </button>
        </div>

        {showNew && (
          <div className="rounded-xl border border-[hsl(var(--primary))]/40 bg-[hsl(var(--background))] p-3 mb-4 space-y-2" data-testid="vn-newchar-form">
            <div className="flex flex-wrap gap-2">
              <input value={ncName} onChange={(e) => setNcName(e.target.value)} placeholder="Name (e.g. Kaito)" data-testid="vn-nc-name"
                className="flex-1 min-w-[140px] rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-3 py-2 text-sm" />
              <select value={ncKind} onChange={(e) => setNcKind(e.target.value)} data-testid="vn-nc-kind"
                className="rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-3 py-2 text-sm capitalize">
                {KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
              </select>
            </div>
            <textarea value={ncDesc} onChange={(e) => setNcDesc(e.target.value)} rows={2} placeholder="Describe them — look, vibe, personality…" data-testid="vn-nc-desc"
              className="w-full rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-3 py-2 text-sm resize-none" />
            <div className="flex justify-end">
              <button onClick={quickCreate} disabled={creating || !(ncDesc.trim() || ncName.trim())} data-testid="vn-nc-create"
                className="inline-flex items-center gap-1.5 rounded-full bg-[hsl(var(--primary))] text-white px-4 py-2 text-sm font-semibold hover:opacity-90 disabled:opacity-50">
                {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />} {creating ? "Creating…" : "Create character"}
              </button>
            </div>
          </div>
        )}

        {chars.length === 0 ? (
          <p className="text-sm text-[hsl(var(--muted-foreground))] py-6 text-center">No characters yet — create one above, or head to Character Studio.</p>
        ) : (
          <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-3">
            {chars.map((c) => {
              const sel = selected.includes(c.id);
              const moods = Object.keys(c.sprites || {}).length;
              return (
                <button key={c.id} onClick={() => toggle(c.id)} data-testid={`vn-char-${c.id}`}
                  className={`relative text-center rounded-xl border p-2 transition-all ${sel ? "border-[hsl(var(--primary))] ring-2 ring-[hsl(var(--primary))]/40 bg-[hsl(var(--primary))]/5" : "border-[hsl(var(--border))] hover:border-[hsl(var(--primary))]/50"}`}>
                  <div className="aspect-square rounded-lg overflow-hidden bg-[hsl(var(--secondary))] grid place-items-center mb-1">
                    {c.image ? <img src={asset(c.image)} alt={c.name} className="w-full h-full object-cover" /> : <span className="text-3xl">{c.emoji || "🎭"}</span>}
                  </div>
                  <p className="text-[11px] font-semibold truncate">{c.name}</p>
                  {moods > 0 && <span className="text-[9px] text-[hsl(var(--primary))]">{moods} moods</span>}
                  {sel && <span className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-[hsl(var(--primary))] text-white grid place-items-center"><Check className="w-3 h-3" /></span>}
                </button>
              );
            })}
          </div>
        )}
        <p className="text-[11px] text-[hsl(var(--muted-foreground))] mt-3">Tip: characters with <b>Expression Sprites</b> (generated in Character Studio) will change faces to match each line.</p>
      </section>

      {/* Premise + generate */}
      <section className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-5 mb-8">
        <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Scene title (optional)" data-testid="vn-title-input"
          className="w-full rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] px-3 py-2 text-sm mb-2" />
        <textarea value={premise} onChange={(e) => setPremise(e.target.value)} rows={3} data-testid="vn-premise-input"
          placeholder="What happens? e.g. Two rival duelists meet at dawn before the final tournament and must decide what their rivalry really means."
          className="w-full rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] px-3 py-2.5 text-sm resize-none mb-3" />
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="inline-flex rounded-full border border-[hsl(var(--border))] p-1" data-testid="vn-depth-toggle">
            <button onClick={() => setDepth("light")} data-testid="vn-depth-light"
              className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${depth === "light" ? "bg-[hsl(var(--primary))] text-white" : "text-[hsl(var(--muted-foreground))]"}`}>✦ Light (1 ending)</button>
            <button onClick={() => setDepth("branching")} data-testid="vn-depth-branching"
              className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${depth === "branching" ? "bg-[hsl(var(--primary))] text-white" : "text-[hsl(var(--muted-foreground))]"}`}>❖ Branching (multiple endings)</button>
          </div>
          <button onClick={write} disabled={premise.trim().length < 4 || writing} data-testid="vn-write-btn"
            className="inline-flex items-center gap-2 rounded-full bg-[hsl(var(--primary))] text-white px-5 py-2.5 text-sm font-semibold hover:opacity-90 disabled:opacity-50">
            {writing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} {writing ? "Writing your scene…" : "Write my scene"}
          </button>
        </div>
      </section>

      {/* Existing scenes */}
      <h2 className="text-sm font-bold mb-3 text-[hsl(var(--muted-foreground))]">Your scenes</h2>
      {scenes.length === 0 ? (
        <p className="text-sm text-[hsl(var(--muted-foreground))]">No scenes yet — write your first one above!</p>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="vn-scenes-list">
          {scenes.map((s) => (
            <button key={s.id} onClick={() => navigate(`/app/vn/${s.id}`)} data-testid={`vn-scene-card-${s.id}`}
              className="text-left rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4 hover:border-[hsl(var(--primary))]/50 hover:-translate-y-0.5 transition-all">
              <div className="flex items-center justify-between gap-2 mb-1">
                <h3 className="font-bold truncate">{s.title}</h3>
                <ArrowRight className="w-4 h-4 text-[hsl(var(--muted-foreground))] shrink-0" />
              </div>
              <p className="text-[11px] text-[hsl(var(--muted-foreground))] mb-2">{s.node_count} beats · {s.ending_count} ending{s.ending_count === 1 ? "" : "s"} · {(s.cast || []).length} cast</p>
              <div className="flex items-center gap-2 text-[11px]">
                <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 ${s.bg_status === "ready" ? "bg-emerald-500/15 text-emerald-500" : s.bg_status === "building" ? "bg-amber-500/15 text-amber-500" : "bg-[hsl(var(--secondary))] text-[hsl(var(--muted-foreground))]"}`}>
                  <Play className="w-3 h-3" /> {s.bg_status === "ready" ? "Backgrounds ready" : s.bg_status === "building" ? "Painting…" : "No backgrounds"}
                </span>
                {s.share_code && <span className="inline-flex items-center gap-1 text-[hsl(var(--primary))]"><Share2 className="w-3 h-3" /> shared</span>}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
